import type React from 'react';
import { Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const ROW_COUNT = 9;

type StatementSkeletonProps = {
    /** Literal testid prefix, e.g. `balance-sheet` / `profit-loss`. */
    testIdPrefix: string;
    /** Reserve space for the Balance Sheet's balance-check bar. */
    showBalanceCheck?: boolean;
};

/**
 * Loading placeholder for the statement **body only** — the optional balance
 * check plus indented rows with a right-aligned amount, so the page does not
 * jump when data arrives.
 *
 * Per the repo's loading-state standard the period controls and the Print button
 * above this keep their real labels and are disabled; a skeleton stands in for
 * dynamic content alone.
 */
export const StatementSkeleton: React.FC<StatementSkeletonProps> = ({
    testIdPrefix,
    showBalanceCheck,
}) => (
    <div
        className="flex flex-col gap-4"
        {...getNodeTestIdProp(`${testIdPrefix}-skeleton`)}
    >
        {showBalanceCheck ? (
            <Skeleton className="h-16 w-full rounded-xl" />
        ) : null}

        <div className="overflow-hidden rounded-2xl border border-separator bg-surface">
            <div className="flex items-center gap-4 border-b border-separator px-4 py-3">
                <Skeleton className="h-3 w-40 rounded" />
                <Skeleton className="ml-auto h-3 w-24 rounded" />
            </div>
            {Array.from({ length: ROW_COUNT }).map((_, index) => (
                <div
                    className="flex items-center gap-4 px-4 py-2.5"
                    // Static placeholder rows — index key is safe here.
                    key={`${testIdPrefix}-skeleton-row-${index}`}
                    style={{ paddingInlineStart: 16 + (index % 3) * 24 }}
                >
                    <Skeleton className="h-4 w-48 rounded" />
                    <Skeleton className="ml-auto h-4 w-24 rounded" />
                </div>
            ))}
        </div>
    </div>
);
